<template>
  <generic-modal
    :title="$t('checkout') || 'Finalizar Compra'"
    ref="generic_modal"
    class="payment-modal"
    size="fullscreen"
  >
    <!-- Información del cliente -->
    <div class="payment-section">
      <div class="section-header">
        <h3 class="section-title">
          <i class="fa fa-user section-icon"></i>
          {{ $t("customer information") }}
        </h3>
      </div>

      <div class="form-grid">
        <div class="form-group">
          <label class="form-label">{{ $t("full name") }}</label>
          <div class="form-control">
            <input
              class="form-input"
              type="text"
              placeholder="Luis Robledo"
              v-model="meta.name"
              required
            />
            <i class="fa fa-user form-icon"></i>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t("email") }}</label>
          <div class="form-control">
            <input
              class="form-input"
              type="email"
              placeholder="correo@ejemplo.com"
              v-model="meta.email"
              required
            />
            <i class="fa fa-envelope form-icon"></i>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t("address") }}</label>
          <div class="form-control">
            <input
              class="form-input"
              type="text"
              :placeholder="$t('address') || 'Dirección completa'"
              v-model="meta.address"
              required
            />
            <i class="fa fa-map-marker form-icon"></i>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t("phone") }}</label>
          <div class="form-control">
            <input
              class="form-input"
              type="text"
              :placeholder="$t('phone') || 'Número de teléfono'"
              v-model="meta.phone"
              required
            />
            <i class="fa fa-phone form-icon"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Método de pago -->
    <div class="payment-section">
      <div class="section-header">
        <h3 class="section-title">
          <i class="fa fa-credit-card section-icon"></i>
          {{ $t("payment method") }}
        </h3>
      </div>

      <div class="payment-methods">
        <div
          class="payment-method-card"
          :class="{ 'is-selected': meta.payment_method === 'bank_deposit' }"
          @click="meta.payment_method = 'bank_deposit'"
        >
          <div class="payment-method-icon">
            <i class="fa fa-university"></i>
          </div>
          <div class="payment-method-content">
            <h4 class="payment-method-title">
              {{ $t("deposit/transference") }}
            </h4>
            <p class="payment-method-description">
              {{
                $t("bank transfer description") ||
                "Transfiere directamente a nuestra cuenta bancaria"
              }}
            </p>
          </div>
          <div class="payment-method-check">
            <i class="fa fa-check"></i>
          </div>
        </div>

        <div
          class="payment-method-card"
          :class="{ 'is-selected': meta.payment_method === 'credit_card' }"
          @click="meta.payment_method = 'credit_card'"
        >
          <div class="payment-method-icon">
            <i class="fa fa-credit-card"></i>
          </div>
          <div class="payment-method-content">
            <h4 class="payment-method-title">
              {{ $t("card") }}
            </h4>
            <p class="payment-method-description">
              {{ $t("card description") }}
            </p>
          </div>
          <div class="payment-method-check">
            <i class="fa fa-check"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Información de tarjeta de crédito -->
    <div class="payment-section" v-if="meta.payment_method === 'credit_card'">
      <div class="section-header">
        <h3 class="section-title">
          <i class="fa fa-lock section-icon"></i>
          {{ $t("card information") }}
        </h3>
        <div class="security-badge">
          <i class="fa fa-shield"></i>
          {{ $t("secure payment") }}
        </div>
      </div>

      <div class="card-form">
        <div class="card-preview">
          <div class="credit-card">
            <div class="card-number-display">
              {{
                formatCardNumber(card_info.card_number) || "•••• •••• •••• ••••"
              }}
            </div>
            <div class="card-details">
              <div class="card-holder">
                {{ meta.name || "NOMBRE DEL TITULAR" }}
              </div>
              <div class="card-expiry">
                {{ card_info.expire_date || "MM/YY" }}
              </div>
            </div>
          </div>
        </div>

        <div class="card-inputs">
          <div class="form-group full-width">
            <label class="form-label">{{ $t("card number") }}</label>
            <div class="form-control">
              <input
                :disabled="is_processing_payment || payment_processed"
                class="form-input"
                type="text"
                placeholder="1234 5678 9012 3456"
                v-model="card_info.card_number"
                @input="formatCardInput"
                maxlength="19"
                required
              />
              <i class="fa fa-credit-card form-icon"></i>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">{{ $t("expiration date") }}</label>
              <div class="form-control">
                <input
                  :disabled="is_processing_payment || payment_processed"
                  class="form-input"
                  type="text"
                  placeholder="MM/YY"
                  v-model="card_info.expire_date"
                  @input="formatExpiryInput"
                  maxlength="5"
                  required
                />
                <i class="fa fa-calendar form-icon"></i>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">{{ $t("cvv") }}</label>
              <div class="form-control">
                <input
                  :disabled="is_processing_payment || payment_processed"
                  class="form-input"
                  type="text"
                  placeholder="123"
                  v-model="card_info.cvv"
                  maxlength="4"
                  required
                />
                <i class="fa fa-lock form-icon"></i>
              </div>
            </div>
          </div>

          <div class="payment-action">
            <button
              class="btn btn-primary btn-pay"
              @click="make_payment"
              :disabled="card_info_missing || is_processing_payment"
              :class="{
                'is-loading': is_processing_payment,
                'is-hidden': payment_processed,
              }"
            >
              <span class="btn-icon">
                <i class="fa fa-lock"></i>
              </span>
              <span>{{ $t("process payment") }}</span>
              <span class="btn-amount">{{
                format_currency(cart_total.toFixed(2))
              }}</span>
            </button>

            <div class="payment-success" v-if="payment_processed">
              <div class="success-icon">
                <i class="fa fa-check"></i>
              </div>
              <div class="success-text">
                <h4>{{ $t("payment succeeded") }}</h4>
                <p>
                  {{
                    $t("payment processed successfully") ||
                    "Tu pago ha sido procesado correctamente"
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Información de depósito bancario -->
    <div class="payment-section" v-if="meta.payment_method === 'bank_deposit'">
      <div class="section-header">
        <h3 class="section-title">
          <i class="fa fa-university section-icon"></i>
          {{ $t("bank transfer details") }}
        </h3>
      </div>

      <div class="bank-info-card">
        <div class="bank-info-header">
          <i class="fa fa-university"></i>
          <h4>
            {{ $t("transfer to this account") }}
          </h4>
        </div>
        <div class="bank-details">
          <div class="bank-detail-item">
            <span class="detail-label">{{ $t("bank") }}:</span>
            <span class="detail-value">Banco G&T Continental</span>
          </div>
          <div class="bank-detail-item">
            <span class="detail-label">{{ $t("account holder") }}:</span>
            <span class="detail-value">Jorge Robledo</span>
          </div>
          <div class="bank-detail-item">
            <span class="detail-label">{{ $t("account number") }}:</span>
            <span class="detail-value">123456789</span>
            <button
              class="copy-btn"
              @click="copyToClipboard('123456789')"
              title="Copiar"
            >
              <i class="fa fa-copy"></i>
            </button>
          </div>
          <div class="bank-detail-item">
            <span class="detail-label">{{ $t("account type") }}:</span>
            <span class="detail-value">Cuenta monetaria</span>
          </div>
        </div>
      </div>

      <div class="transfer-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">{{ $t("payment date") }}</label>
            <div class="form-control">
              <input
                class="form-input"
                type="date"
                v-model="meta.payment_detail.date"
                required
              />
              <i class="fa fa-calendar form-icon"></i>
            </div>
          </div>
        </div>

        <div class="upload-section">
          <label class="form-label">{{ $t("payment voucher") }}</label>
          <div class="upload-area">
            <generic-uploader
              ref="generic_uploader"
              :idle_label="
                $t('drop payment voucher here') ||
                'Arrastra tu comprobante aquí o haz clic para seleccionar'
              "
              endpoint_url="/online-store/order/upload-voucher"
              @error="error_uploading"
              @success="success_uploading"
              :allow_multiple="false"
              class="custom-uploader"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Resumen del pedido -->
    <div class="order-summary">
      <div class="summary-header">
        <h3 class="section-title">
          <i class="fa fa-shopping-cart section-icon"></i>
          {{ $t("order summary") }}
        </h3>
      </div>
      <div class="summary-content">
        <div class="total-amount">
          <span class="total-label">{{ $t("total to pay") }}:</span>
          <span class="total-value">{{
            format_currency(cart_total.toFixed(2))
          }}</span>
        </div>
        <div class="items-count">
          {{ cart_items.length }}
          {{ cart_items.length === 1 ? "artículo" : "artículos" }}
        </div>
      </div>
    </div>

    <!-- Mensaje de error -->
    <div class="error-message" v-if="error">
      <div class="error-content">
        <i class="fa fa-exclamation-triangle"></i>
        <p v-html="error"></p>
      </div>
    </div>

    <template v-slot:footer>
      <div class="modal-actions">
        <button class="btn btn-secondary" @click="close_modal">
          <span class="btn-icon">
            <i class="fa fa-times"></i>
          </span>
          <span>{{ $t("cancel") }}</span>
        </button>

        <button
          class="btn btn-primary btn-order"
          @click="make_order"
          :disabled="is_form_invalid"
        >
          <span class="btn-icon">
            <i class="fa fa-check"></i>
          </span>
          <span>{{ $t("place order") }}</span>
        </button>
      </div>
    </template>
  </generic-modal>
</template>
<script>
import {
  put,
  error_message,
  get,
  post,
  error_alert,
  generate_error_message_text,
  get_currency_settings,
  format_currency,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import GenericUploader from "@/components/generic_uploader/Index.vue";

export default {
  name: "OnlineStorePaymentModal",
  components: {
    GenericModal,
    GenericUploader,
  },
  data() {
    return {
      meta: { settings: {}, payment_detail: {} },
      card_info: {},
      cart_items: [],
      is_processing_payment: false,
      payment_processed: false,
      error: "",
    };
  },
  computed: {
    card_info_missing() {
      let v = this;
      return (
        !v.card_info.card_number || !v.card_info.cvv || !v.card_info.expire_date
      );
    },
    is_form_invalid() {
      let v = this;
      // return false;
      let first_validation =
        !v.meta.name || !v.meta.email || !v.meta.payment_method;

      let second_validation = true;
      if (v.meta.payment_method === "credit_card") {
        second_validation = !v.payment_processed;
      } else if (v.meta.payment_method === "bank_deposit") {
        second_validation =
          !v.meta.payment_detail.date || !v.meta.payment_detail.voucher_file;
      }

      return first_validation || second_validation;
    },
    cart_total() {
      let v = this;
      return v.cart_items.reduce((total, item) => {
        return total + item.price * item.quantity;
      }, 0);
    },
  },
  methods: {
    format_currency,
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    show(cart_items) {
      let v = this;
      v.set_initial_state();
      v.cart_items = cart_items;
      v.$refs.generic_modal.show_modal();
    },
    formatCardNumber(value) {
      if (!value) return "";
      return value
        .replace(/\s/g, "")
        .replace(/(.{4})/g, "$1 ")
        .trim();
    },
    formatCardInput(event) {
      let value = event.target.value.replace(/\s/g, "").replace(/[^0-9]/gi, "");
      let formattedValue = value.match(/.{1,4}/g)?.join(" ") || value;
      this.card_info.card_number = formattedValue;
    },
    formatExpiryInput(event) {
      let value = event.target.value.replace(/\D/g, "");
      if (value.length >= 2) {
        value = value.substring(0, 2) + "/" + value.substring(2, 4);
      }
      this.card_info.expire_date = value;
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$toast.success(this.$t("copied to clipboard"));
        })
        .catch(() => {
          // Fallback para navegadores más antiguos
          const textArea = document.createElement("textarea");
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          this.$toast.success(this.$t("copied to clipboard"));
        });
    },
    make_payment() {
      try {
        let v = this;
        let data = { ...v.card_info, amount: v.cart_total };
        v.is_processing_payment = true;
        v.$root.loader(true);
        post("/online-store/order/pay", data)
          .then((r) => {
            setTimeout(() => {
              v.payment_processed = true;
              v.meta.payment_detail = r;
            }, 3000);
          })
          .catch((error) => {
            console.error(error);
            v.error = generate_error_message_text(error);
          })
          .finally(() => {
            setTimeout(() => {
              v.is_processing_payment = false;
              v.$root.loader(false);
            }, 3000);
          });
      } catch (error) {
        console.error(error);
      }
    },
    make_order() {
      try {
        let v = this;
        let data = {
          ...v.meta,
          items: v.cart_items.map((item) => {
            return {
              product_id: item.product_id,
              name: item.name,
              price: item.price,
              quantity: item.quantity,
              settings: {},
            };
          }),
          total: v.cart_total,
        };
        if (data.payment_method === "credit_card") data.payment_method = "card";
        else if (data.payment_method === "bank_deposit")
          data.payment_method = "deposit";
        v.$root.loader(true);
        post("/online-store/order", data)
          .then((r) => {
            v.$emit("order_made", r);
            v.close_modal();
          })
          .catch((error) => {
            v.error = generate_error_message_text(error);
          })
          .finally(() => {
            v.$root.loader(false);
          });
      } catch (error) {
        console.error(error);
      }
    },
    set_initial_state() {
      let v = this;
      v.meta = { settings: {}, payment_detail: {} };
      v.card_info = {};
      v.cart_items = [];
      v.is_processing_payment = false;
      v.payment_processed = false;
      v.error = "";
    },
    error_uploading(error) {
      let v = this;
      v.error = generate_error_message_text(error);
    },
    success_uploading(response) {
      let v = this;
      let resp = JSON.parse(response.serverId);
      if (resp && resp.filepath)
        v.meta.payment_detail.voucher_file = resp.filepath;
    },
  },
  mounted() {},
  watch: {},
};
</script>
<style scoped>
/* Variables CSS para el modal de pago */
:root {
  --payment-primary: #667eea;
  --payment-success: #4ecdc4;
  --payment-danger: #ff6b6b;
  --payment-warning: #feca57;
  --payment-dark: #2c3e50;
  --payment-light: #f8f9fa;
  --payment-border: #e9ecef;
  --payment-gray: #6c757d;
  --payment-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  --payment-shadow-light: 0 2px 10px rgba(0, 0, 0, 0.05);
  --payment-radius: 12px;
  --payment-transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Estilos del modal */
.payment-modal :deep(.modal-card) {
  max-width: 800px;
  border-radius: var(--payment-radius);
  overflow: hidden;
}

.payment-modal :deep(.modal-card-head) {
  background: linear-gradient(135deg, var(--payment-primary) 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 2rem;
}

.payment-modal :deep(.modal-card-title) {
  color: white !important;
  font-size: 1.5rem;
  font-weight: 600;
}

/* Secciones del formulario */
.payment-section {
  margin-bottom: 2.5rem;
  padding: 0 0.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--payment-border);
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--payment-dark);
  margin: 0;
}

.section-icon {
  margin-right: 0.75rem;
  color: var(--payment-primary);
  font-size: 1.2rem;
}

.security-badge {
  display: flex;
  align-items: center;
  background: #e8f5e8;
  color: #2d5a2d;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.security-badge i {
  margin-right: 0.5rem;
}

/* Grid de formularios */
.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
}

.form-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--payment-dark);
  margin-bottom: 0.5rem;
}

.form-control {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 2px solid var(--payment-border);
  border-radius: var(--payment-radius);
  font-size: 1rem;
  transition: var(--payment-transition);
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: var(--payment-primary);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input:disabled {
  background: #f8f9fa;
  color: var(--payment-gray);
  cursor: not-allowed;
}

.form-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--payment-gray);
  font-size: 1rem;
}

/* Métodos de pago */
.payment-methods {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.payment-method-card {
  border: 2px solid var(--payment-border);
  border-radius: var(--payment-radius);
  padding: 1.5rem;
  cursor: pointer;
  transition: var(--payment-transition);
  position: relative;
  background: white;
}

.payment-method-card:hover {
  border-color: var(--payment-primary);
  box-shadow: var(--payment-shadow-light);
}

.payment-method-card.is-selected {
  border-color: var(--payment-primary);
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.05) 0%,
    rgba(118, 75, 162, 0.05) 100%
  );
}

.payment-method-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--payment-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  transition: var(--payment-transition);
}

.payment-method-card.is-selected .payment-method-icon {
  background: var(--payment-primary);
  color: white;
}

.payment-method-icon i {
  font-size: 1.5rem;
  color: var(--payment-primary);
}

.payment-method-card.is-selected .payment-method-icon i {
  color: white;
}

.payment-method-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--payment-dark);
  margin-bottom: 0.5rem;
}

.payment-method-description {
  color: var(--payment-gray);
  font-size: 0.9rem;
  line-height: 1.4;
}

.payment-method-check {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--payment-success);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0);
  transition: var(--payment-transition);
}

.payment-method-card.is-selected .payment-method-check {
  opacity: 1;
  transform: scale(1);
}

/* Tarjeta de crédito */
.card-form {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 2rem;
  align-items: start;
}

.card-preview {
  position: sticky;
  top: 1rem;
}

.credit-card {
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, var(--payment-primary) 0%, #764ba2 100%);
  border-radius: 15px;
  padding: 1.5rem;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: var(--payment-shadow);
  position: relative;
  overflow: hidden;
}

.credit-card::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.card-number-display {
  font-size: 1.3rem;
  font-weight: 600;
  letter-spacing: 2px;
  font-family: "Courier New", monospace;
}

.card-details {
  display: flex;
  justify-content: space-between;
  align-items: end;
}

.card-holder {
  font-size: 0.9rem;
  text-transform: uppercase;
  font-weight: 500;
}

.card-expiry {
  font-size: 0.9rem;
  font-weight: 500;
}

.card-inputs {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Información bancaria */
.bank-info-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid var(--payment-border);
  border-radius: var(--payment-radius);
  padding: 2rem;
  margin-bottom: 2rem;
}

.bank-info-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  color: var(--payment-dark);
}

.bank-info-header i {
  font-size: 1.5rem;
  margin-right: 0.75rem;
  color: var(--payment-primary);
}

.bank-info-header h4 {
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

.bank-details {
  display: grid;
  gap: 1rem;
}

.bank-detail-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--payment-border);
}

.detail-label {
  font-weight: 500;
  color: var(--payment-gray);
}

.detail-value {
  font-weight: 600;
  color: var(--payment-dark);
}

.copy-btn {
  background: var(--payment-primary);
  color: white;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--payment-transition);
  margin-left: 1rem;
}

.copy-btn:hover {
  background: #5a6fd8;
  transform: scale(1.05);
}

/* Área de subida */
.upload-section {
  margin-top: 1.5rem;
}

.upload-area {
  border: 2px dashed var(--payment-border);
  border-radius: var(--payment-radius);
  padding: 2rem;
  text-align: center;
  transition: var(--payment-transition);
}

.upload-area:hover {
  border-color: var(--payment-primary);
  background: rgba(102, 126, 234, 0.02);
}

/* Botones */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border: 2px solid transparent;
  border-radius: var(--payment-radius);
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: var(--payment-transition);
  gap: 0.5rem;
  min-height: 48px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--payment-primary) 0%, #764ba2 100%);
  color: white;
  border-color: var(--payment-primary);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--payment-shadow);
}

.btn-secondary {
  background: white;
  color: var(--payment-gray);
  border-color: var(--payment-border);
}

.btn-secondary:hover {
  background: var(--payment-light);
  border-color: var(--payment-gray);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn-pay {
  width: 100%;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  position: relative;
}

.btn-amount {
  margin-left: auto;
  font-weight: 700;
}

.btn-order {
  background: linear-gradient(135deg, var(--payment-success) 0%, #44a08d 100%);
  border-color: var(--payment-success);
}

/* Estados de éxito */
.payment-success {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: #e8f5e8;
  border: 1px solid #c3e6c3;
  border-radius: var(--payment-radius);
  color: #2d5a2d;
}

.success-icon {
  width: 50px;
  height: 50px;
  background: var(--payment-success);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.success-text h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.success-text p {
  margin: 0;
  opacity: 0.8;
}

/* Resumen del pedido */
.order-summary {
  background: linear-gradient(135deg, var(--payment-light) 0%, #ffffff 100%);
  border: 1px solid var(--payment-border);
  border-radius: var(--payment-radius);
  padding: 2rem;
  margin-bottom: 2rem;
}

.summary-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.total-amount {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.total-label {
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--payment-dark);
}

.total-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--payment-primary);
}

.items-count {
  color: var(--payment-gray);
  font-size: 0.95rem;
}

/* Mensajes de error */
.error-message {
  background: #ffe6e6;
  border: 1px solid #ffcccc;
  border-radius: var(--payment-radius);
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.error-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: #cc0000;
}

.error-content i {
  font-size: 1.2rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

/* Footer del modal */
.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem;
  background: var(--payment-light);
  border-top: 1px solid var(--payment-border);
}

/* Estados de carga */
.is-loading {
  position: relative;
  color: transparent !important;
}

.is-loading::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .payment-methods {
    grid-template-columns: 1fr;
  }

  .card-form {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .summary-content {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .total-amount {
    justify-content: space-between;
  }

  .modal-actions {
    flex-direction: column;
  }

  .payment-modal :deep(.modal-card) {
    margin: 1rem;
    max-width: calc(100vw - 2rem);
  }
}

@media (max-width: 480px) {
  .payment-modal :deep(.modal-card-head) {
    padding: 1.5rem;
  }

  .payment-section {
    padding: 0;
  }

  .bank-info-card {
    padding: 1.5rem;
  }

  .order-summary {
    padding: 1.5rem;
  }
}
</style>
